import { z } from 'zod';
import { CheckoutModeSchema, PurchaseOriginSchema } from '../indicator/indicator';
import { AncillaryUnavailableReasonSchema } from '../../flight_ancillary/views';
import {
  PRODUCT_EVENT_SENTINELS,
  ProductEventHostSchema,
  type ProductEventRecord,
} from './product_event';
import { Taxi99ErrorCodeSchema } from '../../mobility/taxi99_errors.js';
import {
  RideSuggestionKindSchema,
  RideSuggestionSurfaceSchema,
} from '../../mobility/ride_suggestion_wire.js';

export * from './product_event';

const TRIGGER = z.enum(['user', 'auto']);

/** Por que o mapa do Google não veio no card `consult_places` — a ÚNICA
 *  declaração do vocabulário: o kit deriva o tipo daqui, e o evento
 *  `map_load_failed` valida contra o mesmo enum. `no_tiles` é o mapa que nasce
 *  (SDK carregado, `new Map` sem erro) e não pinta tile à vista no prazo (#4034). */
export const MapLoadCauseSchema = z.enum([
  'script_error',
  'timeout',
  'no_api',
  'init_error',
  'no_tiles',
]);
export type MapLoadCause = z.infer<typeof MapLoadCauseSchema>;

/** Por que o ditado por voz falhou, visto do cliente — a ÚNICA declaração do
 *  vocabulário: o web-chat deriva o tipo daqui. `mic_denied`/`mic_unavailable`/
 *  `recorder_unavailable` nem chegam ao servidor; `mic_unavailable` é o microfone
 *  concedido que não abre (sem dispositivo, ocupado, ou host Android sem
 *  MODIFY_AUDIO_SETTINGS — #4496); `http_error` leva `status`; `timeout` é o teto
 *  de 45s do cliente; `unknown` é qualquer rejeição sem tipo. Nunca a mensagem do
 *  erro. */
export const DictationFailReasonSchema = z.enum([
  'mic_denied',
  'mic_unavailable',
  'recorder_unavailable',
  'http_error',
  'timeout',
  'network',
  'empty_transcript',
  'response_invalid',
  'unknown',
]);
export type DictationFailReason = z.infer<typeof DictationFailReasonSchema>;

/** Por que o envio de uma mensagem (POST /messages ou /business/checkout) virou
 *  "failed to send" no cliente (#4519) — a ÚNICA declaração do vocabulário.
 *  `network` é o fetch rejeitado (a resposta nunca chegou — o servidor pode ter
 *  aceitado); `client_error` é a requisição que nem saiu, porque montá-la lançou
 *  (#4915: um corpo que não serializa); `refresh_failed` é o refresh do 401 que
 *  rejeitou; `http_error` leva `status`; `after_accept` é exceção do PRÓPRIO
 *  cliente depois do 2xx. Nunca a mensagem do erro. */
export const SendFailReasonSchema = z.enum([
  'network',
  'client_error',
  'refresh_failed',
  'http_error',
  'after_accept',
  'unknown',
]);
export type SendFailReason = z.infer<typeof SendFailReasonSchema>;

/** A classe do erro que o cliente não tipou (#4915) ou do fetch sem resposta
 *  (`network`, #5321: `TypeError` da rede, `TimeoutError`/`AbortError` do
 *  navegador) — só o NOME de um erro nativo,
 *  nunca a mensagem (ela pode carregar dado do viajante); o resto é `other`. Sem
 *  isto, `unknown` não dizia nem se foi um `TypeError`. */
export const ClientErrorNameSchema = z.enum([
  'Error',
  'TypeError',
  'RangeError',
  'SyntaxError',
  'ReferenceError',
  'TimeoutError',
  'AbortError',
  'other',
]);
export type ClientErrorName = z.infer<typeof ClientErrorNameSchema>;

const PRODUCT = z.enum(['flight', 'hotel', 'rental', 'mobility', 'bus']);

/** Etapa do funil (#5536): onde a compra começou e, a partir do checkout, onde foi enviada. */
const FUNNEL_MODE = {
  mode: PurchaseOriginSchema.optional(),
  checkoutMode: CheckoutModeSchema.optional(),
};

/** A porta de reserva clicada (#5536). A home desktop segue no `home_tile_clicked`. */
export const ActionDoorSchema = z.enum([
  'home_mobile',
  'trip_book_tab',
  'sidebar_menu',
  'sidebar_fab',
  'post_purchase',
  'booking_tabs',
]);
export type ActionDoor = z.infer<typeof ActionDoorSchema>;

/** Por onde a jornada de compra entrou: a porta clicada, `home` (tile da home desktop), `chat`
 *  (etapa no chat), `direct` (bancada sem porta na sessão) ou `none` (emissão: o pedido não tem
 *  sessão). Mora aqui, e não em `indicator.ts`, porque este arquivo já importa aquele. */
export const FunnelEntrySchema = z.enum([
  ...ActionDoorSchema.options,
  'home',
  'chat',
  'direct',
  'none',
]);

/** O que a porta abre: a bancada de um produto ou o pedido à IA (`chat`). */
export const ActionProductSchema = z.enum(['flight', 'hotel', 'rental', 'bus', 'mobility', 'chat']);
export type ActionProduct = z.infer<typeof ActionProductSchema>;

/** Tela do web-chat no `page_viewed`. */
export const PageRouteSchema = z.enum([
  'home',
  'chat',
  'trip',
  'booking',
  'hotel_booking',
  'rental_booking',
  'bus_booking',
  'mobility_booking',
  'approvals',
  'requested',
  'vouchers',
  'group_events',
  'profile',
  'report',
  'wallet',
  'wallet_refund',
  'km_refund',
  'ride_batches',
  'ride_link',
  'not_found',
]);
export type PageRoute = z.infer<typeof PageRouteSchema>;

/** A porta por onde a corrida com a 99 foi aberta (spec 2026-09-29 §5.2). `live_activity` = o
 *  botão "Pedir corrida com 99" do card do voo pousado (spec flight-live-activity D40). */
const MOBILITY99_DOOR = z.enum(['plus_menu', 'sidebar_fab', 'live_activity', 'home', 'chat']);

/** Registry v1 — TODA prop de string é enum (privacidade estrutural; ver spec §4.1).
 *  Evento novo entra AQUI antes de qualquer instrumentação no front. */
export const PRODUCT_EVENT_SCHEMAS = {
  session_started: z.object({}).strict(),
  conversation_started: z.object({}).strict(),
  message_sent: z
    .object({
      source: z.enum(['composer', 'card', 'home_tile', 'trip_tile', 'deep_link', 'pending_thread']),
      domainHint: z.enum(['flight', 'hotel', 'rental', 'mobility', 'bus', 'events']).optional(),
      /** Quantas menções (`@viajante`, `@local`, `@data`, `@viagem`, #2907) o turno
       *  carregou. Só a CONTAGEM — o conteúdo é PII e fica no wire da conversa. Ausente
       *  ⇒ zero (o evento segue igual ao de antes do recurso). */
      mentions: z.number().int().positive().optional(),
    })
    .strict(),
  card_rendered: z.object({}).strict(),
  card_action: z
    .object({
      actionType: z.enum([
        'reply',
        'interaction',
        'guided-flow',
        'rental-select',
        'hotel-select',
        'mobility-select',
        'hotel-room-select',
        'approval-decision',
        'approval-bulk-decision',
        'product-checkout-submit',
        'planner-preview',
        // O botão do card `ride99_link` (#6889): abre a folha da 99, client-side.
        'open-ride99',
      ]),
      actionId: z
        .enum(['confirm', 'deny', 'approve', 'continue', 'cancel', 'select_option'])
        .optional(),
      trigger: TRIGGER,
    })
    .strict(),
  filter_applied: z
    // Eixos REAIS do FlightFilterState/FlightFilterPanel (não inventar faceta):
    // airlines, airports, stops, réguas de partida/chegada, preço, política,
    // número do voo (#2110).
    .object({
      facet: z.enum([
        'airline',
        'airport',
        'stops',
        'time',
        'arrival_time',
        'price',
        'policy',
        'flight_number',
      ]),
    })
    .strict(),
  drawer_opened: z
    .object({ kind: z.enum(['flight_explorer', 'flight_filter', 'flight_detail']) })
    .strict(),
  checkout_opened: z.object({ product: PRODUCT, ...FUNNEL_MODE }).strict(),
  checkout_submitted: z.object({ product: PRODUCT, ...FUNNEL_MODE }).strict(),
  /** Pedido offline (concierge) aberto e enviado nas bancadas. */
  offline_request_opened: z.object({ product: PRODUCT, ...FUNNEL_MODE }).strict(),
  offline_request_submitted: z.object({ product: PRODUCT, ...FUNNEL_MODE }).strict(),
  /** Busca disparada numa bancada (formulário, recente, barra, URL). */
  search_submitted: z.object({ product: PRODUCT, mode: PurchaseOriginSchema }).strict(),
  /** A lista de resultados da bancada apareceu com ao menos um item, uma vez por busca. */
  offers_shown: z.object({ product: PRODUCT, mode: PurchaseOriginSchema }).strict(),
  action_clicked: z.object({ door: ActionDoorSchema, product: ActionProductSchema }).strict(),
  /** Tela aberta no web-chat: só o tipo, nunca parâmetro da rota (#5536). */
  page_viewed: z.object({ route: PageRouteSchema }).strict(),
  home_tile_clicked: z
    .object({ tile: z.enum(['flight', 'hotel', 'rental', 'mobility', 'bus', 'chat']) })
    .strict(),
  /** `dictation_failed` (prod 22/09: 2 hangs de 30s no Whisper + 1 clipe vazio só
   *  vistos no servidor — o front era mudo). `reason` é o ramo do CLIENTE
   *  (`DictationFailReasonSchema`; `SendFailReasonSchema` em `send_failed`, #4519)
   *  e `status` o HTTP da rota. `refreshed` (o envio passou pelo 401 → refresh),
   *  `durationMs` e `errorName` (a classe do erro em `network`/`client_error`/`unknown`)
   *  só existem em `send_failed`. `catalog_unavailable` (#5319) é o select do
   *  perfil/checkout que ficou só-leitura: `catalog` diz qual, `empty` separa o
   *  200 com lista vazia da falha. Objeto plano e não união: a policy faz
   *  `.partial()` num ZodObject. */
  error_shown: z
    .object({
      kind: z.enum([
        'checkout_phase_error',
        'send_failed',
        'dictation_failed',
        'catalog_unavailable',
      ]),
      catalog: z.enum(['profile_countries', 'checkout_countries']).optional(),
      empty: z.boolean().optional(),
      reason: z.union([DictationFailReasonSchema, SendFailReasonSchema]).optional(),
      status: z.number().int().min(100).max(599).optional(),
      refreshed: z.boolean().optional(),
      durationMs: z.number().int().nonnegative().optional(),
      errorName: ClientErrorNameSchema.optional(),
    })
    .strict(),
  /** Copiar no kit não copiou por NENHUM caminho (#2976, item 5): a Clipboard API
   *  rejeitou/faltou E o `execCommand('copy')` legado falhou/faltou. Os dois
   *  booleanos dizem qual caminho EXISTIA no aparelho — `clipboardApi: true` num
   *  `copy_failed` significa "existia e rejeitou" (iframe sem `clipboard-write`),
   *  `execCommand: false` significa navegador que já removeu o legado. Nunca o
   *  texto: é localizador/endereço. Sem isto a falha só aparecia na tela. */
  copy_failed: z.object({ clipboardApi: z.boolean(), execCommand: z.boolean() }).strict(),
  /** A busca de voo que não virou lista (#2926). `cause` separa o que o cliente
   *  não controla (rede/abort na janela de 30–90s) do que é resposta do servidor
   *  (HTTP não-ok, corpo fora do contrato), e `timeout` nomeia o prazo estourado
   *  (504 do BFF, #4465) e `policy_evaluation` a política do viajante que não
   *  avaliou (422 do BFF, #5376) e `search_expired` a busca vencida no
   *  fornecedor (409 do BFF, #5588); `durationMs` diz se a falha veio da
   *  cauda longa ou de um tombo imediato. Sem isto, o fracasso do explorer não
   *  deixava rastro nenhum e o diagnóstico exigia reconstituir trace a trace. */
  flight_search_failed: z
    .object({
      cause: z.enum([
        'network',
        'http',
        'timeout',
        'policy_evaluation',
        'search_expired',
        'parse',
        'unknown',
      ]),
      durationMs: z.number().int().nonnegative(),
    })
    .strict(),
  /** O SDK do Google Maps não carregou no card `consult_places` (dev 17/09): o
   *  componente só mostrava "não foi possível carregar o mapa" e engolia o motivo.
   *  `cause` separa rede/bloqueio (`script_error`), lentidão (`timeout`), SDK sem a
   *  API (`no_api`), erro ao instanciar o mapa (`init_error`) e mapa vivo que não
   *  pinta tile (`no_tiles`, #4034); `attempt` diz se foi a carga inicial (1), a
   *  repetição automática (2) ou "Tentar de novo" (3+). */
  map_load_failed: z
    .object({ cause: MapLoadCauseSchema, attempt: z.number().int().positive() })
    .strict(),
  message_feedback: z
    .object({ value: z.enum(['up', 'down', 'cleared']), detailed: z.boolean() })
    .strict(),
  /** Nota da sessão (spec session-rating 2026-09-29): cartão mostrado,
   *  respondido (com a nota; `detailed` = motivos/comentário no Ruim) ou
   *  dispensado. Sem ids nem texto — o sinal por conversa é o do Mongo. */
  session_rating: z
    .object({
      action: z.enum(['shown', 'answered', 'dismissed']),
      value: z.enum(['bad', 'fine', 'good']).optional(),
      detailed: z.boolean(),
    })
    .strict(),
  /**
   * O composer "Filtre do seu jeito" do explorer de voos (spec
   * flight-text-filter-chips): o que o JUIZ respondeu, nunca o que a pessoa
   * escreveu — o texto é livre e por isso é PII estrutural, e ele fica no wire do
   * modal-session. `kind` é o veredito (`filter` virou corte; `question`/`new_search`
   * viraram nota; `noise` não virou nada; `error` é o seam que falhou), `chips`/`applied`
   * medem quanto do pedido o painel conseguiu aplicar (o par é o que revela eixo pedido
   * e não suportado) e `judge` diz quem respondeu — o determinístico ou o LLM.
   * Tudo menos `kind`/`latencyMs` é OPCIONAL porque o caminho de ERRO não tem intent
   * nenhum para medir.
   */
  flight_filter_text: z
    .object({
      kind: z.enum(['filter', 'question', 'new_search', 'control', 'noise', 'error']),
      chips: z.number().int().nonnegative().optional(),
      applied: z.number().int().nonnegative().optional(),
      /** Chips guardados para outra perna (R2-D3) — mede quanto do pedido ficou pendente. */
      deferred: z.number().int().nonnegative().optional(),
      /** Só com `kind: 'control'` (§5.6) — qual comando (hoje só "Redefinir todos"). Enum
       *  PLANO de propósito: o schema do domain (`FlightFilterControlSchema`) carrega
       *  `.catch(undefined)`, que engoliria um valor torto calado — aqui, em telemetria, um
       *  valor fora do contrato deve REPROVAR a prop (mesma régua das demais chaves deste
       *  evento), não desaparecer. */
      control: z.enum(['reset']).optional(),
      judge: z.enum(['jev', 'llm', 'unknown']).optional(),
      latencyMs: z.number().int().nonnegative(),
    })
    .strict(),
  /** Ancilares pós-emissão: o mapa de assentos que a tela recebeu para desenhar, contado pelo
   *  mesmo caminho do desenho e conferido contra o resumo do modal-session —
   *  `matchesSummary: false` é a tela mostrando outra coisa que o dado. Só contagens e a faixa
   *  de preço (BRL); nunca poltrona, voo ou localizador. */
  seatmap_rendered: z
    .object({
      legs: z.number().int().nonnegative(),
      sections: z.number().int().nonnegative(),
      seats: z.number().int().nonnegative(),
      open: z.number().int().nonnegative(),
      paid: z.number().int().nonnegative(),
      minPrice: z.number().nonnegative(),
      maxPrice: z.number().nonnegative(),
      matchesSummary: z.boolean(),
    })
    .strict(),
  /** A cotação de bagagem que a tela mostrou: sentidos, malas ofertadas e a faixa de preço. */
  baggage_quote_shown: z
    .object({
      directions: z.number().int().nonnegative(),
      offers: z.number().int().nonnegative(),
      minPrice: z.number().nonnegative(),
      maxPrice: z.number().nonnegative(),
    })
    .strict(),
  /** A tela caiu no contorno: mapa ou cotação indisponível (o motivo do contrato) ou a consulta
   *  que nem respondeu (`load_failed`). */
  ancillary_unavailable: z
    .object({
      product: z.enum(['seat', 'baggage']),
      reason: z.enum([...AncillaryUnavailableReasonSchema.options, 'load_failed']),
    })
    .strict(),
  /** O pedido que saiu da tela e o que voltou dele: `amount` é o total que a pessoa viu (0 quando
   *  o pedido não tem preço — preferência de assento, mala sem cotação). */
  ancillary_request_sent: z
    .object({
      kind: z.enum(['seat_map', 'seat_preference', 'baggage_quote', 'baggage_request']),
      items: z.number().int().nonnegative(),
      amount: z.number().nonnegative(),
      outcome: z.enum(['sent', 'handoff', 'expired', 'already', 'failed']),
    })
    .strict(),
  /** O veredito do Pix no app nativo (#7394): se o app sabe pedir a biometria em "Pagar". `step`
   *  é o toque no Pix ou o "Tentar de novo"; `announced`, se o app anunciou as capacidades;
   *  `bridgeKey`, se mandou a chave do documento; `fallback`, a resposta da biometria pedida
   *  direto quando a chave veio sem o anúncio (`none` = não pedida). A plataforma vem do BFF. */
  pix_guard_resolved: z
    .object({
      step: z.enum(['entry', 'retry']),
      verdict: z.enum(['ok', 'old', 'silent']),
      announced: z.boolean(),
      bridgeKey: z.boolean(),
      fallback: z.enum(['none', 'granted', 'optIn', 'denied']),
    })
    .strict(),
  taxi99_entry_viewed: z.object({ door: MOBILITY99_DOOR }).strict(),
  taxi99_request_started: z.object({ door: MOBILITY99_DOOR }).strict(),
  taxi99_location_denied: z.object({ door: MOBILITY99_DOOR }).strict(),
  taxi99_outside_coverage: z.object({ door: MOBILITY99_DOOR }).strict(),
  /** A partida trocada deixou o destino já na rota a mais de 60 km: ele saiu com aviso (#5129). */
  taxi99_destination_out_of_range: z.object({ door: MOBILITY99_DOOR }).strict(),
  taxi99_ride_requested: z.object({ door: MOBILITY99_DOOR }).strict(),
  taxi99_ride_failed: z.object({ door: MOBILITY99_DOOR, code: Taxi99ErrorCodeSchema }).strict(),
  taxi99_h5_opened: z
    .object({ door: MOBILITY99_DOOR, purpose: z.enum(['ride', 'track', 'help']) })
    .strict(),
  taxi99_h5_closed: z.object({ door: MOBILITY99_DOOR }).strict(),
  // Sugestão de destino da corrida (spec 2026-10-13 §7.5; spec 2026-10-15, Analytics): sem
  // coordenada, sem texto. `surface` é onde a lista apareceu (Home e aba Ride: `home`; folha:
  // `sheet`); `door`, por onde a tela foi aberta. No modo degradado (o pedido falhou e a lista é
  // a crua de favoritos e recentes) não há conjunto: sem `setId`, `rankedBy: 'degraded'`.
  ride_suggestion_shown: z
    .object({
      setId: z.uuid().optional(),
      count: z.number().int().min(0).max(8),
      rankedBy: z.enum(['jev', 'deterministic', 'degraded']),
      hasRecommended: z.boolean(),
      surface: RideSuggestionSurfaceSchema,
      door: MOBILITY99_DOOR,
      degraded: z.boolean(),
    })
    .strict(),
  ride_suggestion_tapped: z
    .object({
      setId: z.uuid().optional(),
      rank: z.number().int().min(1).max(8),
      kind: RideSuggestionKindSchema,
      recommended: z.boolean(),
      surface: RideSuggestionSurfaceSchema,
      door: MOBILITY99_DOOR,
      degraded: z.boolean(),
    })
    .strict(),
} as const;

export type ProductEventName = keyof typeof PRODUCT_EVENT_SCHEMAS;
export const PRODUCT_EVENT_NAMES = Object.keys(PRODUCT_EVENT_SCHEMAS) as ProductEventName[];

/** Normalização no boundary (analytics-api). Pura: kinds conhecidos entram por
 *  parâmetro (domain não importa genui-kit). */
export function applyProductEventPolicy(
  record: ProductEventRecord,
  opts: { knownCardTypes: ReadonlySet<string> },
): ProductEventRecord {
  const schema = (PRODUCT_EVENT_SCHEMAS as Record<string, z.ZodObject>)[record.name];
  let name = record.name;
  let props: ProductEventRecord['props'] = {};
  if (schema) {
    // .pick({[key]: true}) dinâmico lança em zod v4 para chave desconhecida
    // (em vez de falhar o safeParse); .partial() torna os demais campos
    // opcionais e preserva o .strict() do schema — chave fora do schema OU
    // valor fora do enum reprova, o resto passa.
    const partial = schema.partial();
    props = Object.fromEntries(
      Object.entries(record.props).filter(
        ([key, value]) => partial.safeParse({ [key]: value }).success,
      ),
    );
  } else {
    name = PRODUCT_EVENT_SENTINELS.unknownEvent; // string bruta do client não persiste
  }
  const cardType =
    record.cardType === undefined
      ? undefined
      : opts.knownCardTypes.has(record.cardType)
        ? record.cardType
        : PRODUCT_EVENT_SENTINELS.unknownCard;
  const host =
    record.host === undefined
      ? undefined
      : ProductEventHostSchema.safeParse(record.host).success
        ? record.host
        : PRODUCT_EVENT_SENTINELS.unknownHost;
  return {
    ...record,
    name,
    props,
    ...(cardType === undefined ? {} : { cardType }),
    ...(host === undefined ? {} : { host }),
  };
}
